/**
 * ImageGallery Component
 *
 * A component for displaying image attachments as thumbnails in a gallery grid.
 * Supports click to preview, optional delete, and filters non-image attachments.
 *
 * @example
 * ```jsx
 * // Basic usage - pass all attachments, component filters to images only
 * <ImageGallery
 *   attachments={documents}
 *   onClick={(image) => openPreview(image)}
 *   onDelete={(image) => handleDelete(image.id)}
 * />
 *
 * // Read-only mode
 * <ImageGallery
 *   attachments={documents}
 *   readOnly
 * />
 *
 * // Compact variant for smaller displays
 * <ImageGallery
 *   attachments={documents}
 *   variant="compact"
 * />
 * ```
 */

import { XMarkIcon, PhotoIcon } from '@heroicons/react/20/solid';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

/**
 * Check if attachment is an image
 */
const isImage = (attachment) => {
	const name = attachment?.name || attachment?.filename || '';
	const type = attachment?.type || attachment?.mimeType || '';
	const ext = name.split('.').pop()?.toLowerCase();

	return (
		type.startsWith('image/') ||
		['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)
	);
};

/**
 * Get the URL for the image thumbnail/preview
 */
const getImageUrl = (attachment) => {
	return attachment?.url || attachment?.location || attachment?.href || '';
};

/**
 * Single image thumbnail component
 */
const ImageThumbnail = ({
	image,
	onDelete,
	onClick,
	readOnly = false,
	size = 'default',
}) => {
	const url = getImageUrl(image);
	const name = image?.name || image?.filename || 'Image';

	const handleClick = () => {
		if (onClick) {
			onClick(image);
		}
	};

	const handleDelete = (e) => {
		e.stopPropagation();
		if (onDelete) {
			onDelete(image);
		}
	};

	const sizeClasses = {
		compact: 'w-16 h-16',
		default: 'w-24 h-24',
		large: 'w-32 h-32',
	};

	return (
		<div
			className={classNames(
				'relative group rounded-lg overflow-hidden cursor-pointer',
				'border border-gray-200 hover:border-gray-300',
				'transition-all hover:shadow-md',
				sizeClasses[size] || sizeClasses.default
			)}
			onClick={handleClick}
			title={name}
		>
			{url ? (
				<img
					src={url}
					alt={name}
					className="w-full h-full object-cover"
				/>
			) : (
				<div className="w-full h-full flex items-center justify-center bg-gray-100">
					<PhotoIcon className="w-8 h-8 text-gray-400" />
				</div>
			)}

			{/* Hover overlay */}
			<div
				className={classNames(
					'absolute inset-0 bg-black/0 group-hover:bg-black/20',
					'transition-all'
				)}
			/>

			{/* Delete button */}
			{!readOnly && onDelete && (
				<button
					type="button"
					className={classNames(
						'absolute top-1 right-1 p-1 rounded-full',
						'bg-white/80 hover:bg-white',
						'text-gray-500 hover:text-red-600',
						'opacity-0 group-hover:opacity-100',
						'transition-opacity shadow-sm'
					)}
					onClick={handleDelete}
				>
					<XMarkIcon className="h-3.5 w-3.5" />
				</button>
			)}
		</div>
	);
};

/**
 * Main ImageGallery component
 */
const ImageGallery = ({
	attachments = [],
	onDelete,
	onClick,
	readOnly = false,
	variant = 'default', // 'default', 'compact'
	showEmpty = false,
	emptyText = 'No images',
	label,
	className = '',
}) => {
	// Filter to only include images
	const images = attachments?.filter(isImage) || [];
	const hasImages = images.length > 0;

	const size = variant === 'compact' ? 'compact' : 'default';

	if (!hasImages) {
		if (!showEmpty) return null;
		return (
			<div className={className}>
				{label && (
					<div className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">
						{label}
					</div>
				)}
				<p className="text-sm text-gray-500">{emptyText}</p>
			</div>
		);
	}

	return (
		<div className={className}>
			{label && (
				<div className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">
					{label}
				</div>
			)}
			<div
				className={classNames(
					'flex flex-wrap',
					variant === 'compact' ? 'gap-2' : 'gap-3'
				)}
			>
				{images.map((image, idx) => (
					<ImageThumbnail
						key={image?.id || idx}
						image={image}
						onDelete={onDelete}
						onClick={onClick}
						readOnly={readOnly}
						size={size}
					/>
				))}
			</div>
		</div>
	);
};

/**
 * Utility function to filter out images from attachments
 * Use this to get non-image attachments for AttachmentList
 */
export const getNonImageAttachments = (attachments) => {
	return attachments?.filter((a) => !isImage(a)) || [];
};

/**
 * Utility function to get only image attachments
 */
export const getImageAttachments = (attachments) => {
	return attachments?.filter(isImage) || [];
};

export { ImageThumbnail, isImage };
export default ImageGallery;
